/**
 * Watermark utility for applying text or image watermarks to images
 */

/**
 * Convert data URL to Blob
 */
export async function dataURLtoBlob(dataURL: string): Promise<Blob> {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export interface WatermarkOptions {
  type: 'text' | 'image';
  text?: string;
  imageData?: string; // base64 encoded image data
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  opacity: number; // 0-1
  size: number; // percentage (1-100)
  margin?: number; // pixels from edge
  color?: string; // hex color for text
}

export interface WatermarkResult {
  blob: Blob;
  width: number;
  height: number;
  appliedWatermark: boolean;
}

/**
 * Apply watermark to an image blob
 */
export async function applyWatermark(
  imageBlob: Blob,
  options: WatermarkOptions,
  originalImageSize?: number
): Promise<WatermarkResult> {
  try {
    // Create ImageBitmap from blob
    const imageBitmap = await createImageBitmap(imageBlob);

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Draw original image
    ctx.drawImage(imageBitmap, 0, 0);

    // Apply watermark based on type
    if (options.type === 'image' && options.imageData) {
      await applyImageWatermark(ctx, canvas, options);
    } else if (options.type === 'text' && options.text) {
      applyTextWatermark(ctx, canvas, options);
    }

    // Convert canvas to blob with appropriate quality
    const watermarkedBlob = await canvasToBlob(
      canvas,
      imageBlob.type,
      originalImageSize
    );

    // Release ImageBitmap
    imageBitmap.close();

    return {
      blob: watermarkedBlob,
      width: canvas.width,
      height: canvas.height,
      appliedWatermark: true
    };
  } catch (error) {
    console.error('Watermark processing failed:', error);
    throw error;
  }
}

/**
 * Apply image watermark (logo)
 */
async function applyImageWatermark(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  options: WatermarkOptions
): Promise<void> {
  if (!options.imageData) return;

  let logoBlob: Blob;

  // Convert base64 to blob
  if (options.imageData.startsWith('data:')) {
    const arr = options.imageData.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    logoBlob = new Blob([u8arr], { type: mime });
  } else {
    const bstr = atob(options.imageData);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    logoBlob = new Blob([u8arr], { type: 'image/png' });
  }

  const logoBitmap = await createImageBitmap(logoBlob);

  // Calculate logo size
  const sizePercent = options.size || 20;
  const logoScale = (canvas.width * (sizePercent / 100)) / logoBitmap.width;
  const logoWidth = logoBitmap.width * logoScale;
  const logoHeight = logoBitmap.height * logoScale;

  // Calculate position
  const margin = options.margin || 20;
  let logoX: number, logoY: number;

  switch (options.position) {
    case 'top-left':
      logoX = margin;
      logoY = margin;
      break;
    case 'top-right':
      logoX = canvas.width - logoWidth - margin;
      logoY = margin;
      break;
    case 'bottom-left':
      logoX = margin;
      logoY = canvas.height - logoHeight - margin;
      break;
    case 'bottom-right':
      logoX = canvas.width - logoWidth - margin;
      logoY = canvas.height - logoHeight - margin;
      break;
    case 'center':
    default:
      logoX = (canvas.width - logoWidth) / 2;
      logoY = (canvas.height - logoHeight) / 2;
      break;
  }

  // Draw logo with opacity
  ctx.globalAlpha = options.opacity;
  ctx.drawImage(logoBitmap, logoX, logoY, logoWidth, logoHeight);
  ctx.globalAlpha = 1.0;

  // Release ImageBitmap
  logoBitmap.close();
}

/**
 * Apply text watermark
 */
function applyTextWatermark(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  options: WatermarkOptions
): void {
  if (!options.text) return;

  const text = options.text;
  const sizePercent = options.size || 10;
  const opacity = options.opacity ?? 0.7;
  const position = options.position || 'center';
  const margin = options.margin || 50;
  const color = options.color || '#FFFFFF';

  // Calculate font size based on image width
  const imageWidth = canvas.width;
  const actualSizeRatio = 0.02 + (sizePercent / 100) * 0.13;
  const calculatedFontSize = imageWidth * actualSizeRatio;

  // Font size limits
  const minFontSize = 20;
  const maxFontSize = imageWidth * 0.15;
  let fontSize = Math.max(minFontSize, Math.min(maxFontSize, calculatedFontSize));

  // Set font and measure text
  ctx.font = `bold ${fontSize}px Arial`;
  let metrics = ctx.measureText(text);

  // Adjust font size if text is too wide
  const maxTextWidth = canvas.width - (margin * 2);
  if (metrics.width > maxTextWidth) {
    const scaleFactor = maxTextWidth / metrics.width;
    fontSize = Math.max(minFontSize, fontSize * scaleFactor);
    ctx.font = `bold ${fontSize}px Arial`;
    metrics = ctx.measureText(text);
  }

  // Calculate text position
  const textWidth = metrics.width;
  const textHeight = fontSize;
  let textX: number, textY: number;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  switch (position) {
    case 'top-left':
      textX = margin;
      textY = margin;
      break;
    case 'top-right':
      textX = canvas.width - textWidth - margin;
      textY = margin;
      break;
    case 'bottom-left':
      textX = margin;
      textY = canvas.height - textHeight - margin;
      break;
    case 'bottom-right':
      textX = canvas.width - textWidth - margin;
      textY = canvas.height - textHeight - margin;
      break;
    case 'center':
    default:
      textX = (canvas.width - textWidth) / 2;
      textY = (canvas.height - textHeight) / 2;
      break;
  }

  // Add shadow for better visibility
  ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  // Convert hex color to RGBA
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  ctx.fillText(text, textX, textY);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

/**
 * Convert canvas to blob with appropriate quality based on original size
 */
async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  originalSizeMB?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let quality = 0.95; // Default quality

    // Adjust quality based on original size (if compression is OFF)
    if (originalSizeMB !== undefined) {
      if (originalSizeMB < 1) {
        quality = 0.85; // <1MB: 85%
      } else if (originalSizeMB < 3) {
        quality = 0.90; // 1-3MB: 90%
      } else {
        quality = 0.95; // 3MB+: 95%
      }
    }

    if (mimeType === 'image/png') {
      // PNG doesn't support quality parameter
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, 'image/png');
    } else {
      // JPEG with quality
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, 'image/jpeg', quality);
    }
  });
}

/**
 * Convert Blob to Data URL
 */
export async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result && typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to data URL'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Fetch image from URL and return as Blob
 */
export async function fetchImageAsBlob(imageUrl: string): Promise<Blob> {
  if (imageUrl.startsWith('data:')) {
    return dataURLtoBlob(imageUrl)
  } else {
    const response = await fetch(imageUrl)
    if (!response.ok) throw new Error('Failed to fetch image')
    return response.blob()
  }
}

/**
 * Convert service settings to WatermarkOptions
 */
export function convertServiceSettingsToWatermarkOptions(watermarkSettings: any): WatermarkOptions {
  const isCorpLogo = watermarkSettings.watermark_type === 'corporate_logo'
  const isIndividualInput = watermarkSettings.watermark_type === 'individual_input'
  
  return {
    type: isCorpLogo ? 'image' : 'text',
    text: isIndividualInput ? watermarkSettings.individual_input_text : '',
    imageData: isCorpLogo ? watermarkSettings.corporate_logo_file_data : undefined,
    position: 'center', // 現在の実装に合わせて中央固定
    opacity: isCorpLogo
      ? (100 - (watermarkSettings.corporate_logo_transparency || 0)) / 100
      : (100 - (watermarkSettings.individual_input_transparency || 0)) / 100,
    size: isCorpLogo
      ? watermarkSettings.corporate_logo_scale || 100
      : watermarkSettings.individual_input_scale || 100,
    margin: isIndividualInput ? 50 : 20,
    color: isIndividualInput
      ? watermarkSettings.individual_input_color || '#FFFFFF'
      : '#FFFFFF'
  }
}

/**
 * Check if watermark is enabled in service settings
 */
export function isWatermarkEnabled(watermarkSettings: any): boolean {
  return watermarkSettings.watermark_type !== 'none' && (
    (watermarkSettings.watermark_type === 'individual_input' && 
     watermarkSettings.individual_input_text && 
     watermarkSettings.individual_input_is_visible) ||
    (watermarkSettings.watermark_type === 'corporate_logo' && 
     watermarkSettings.corporate_logo_file_data && 
     watermarkSettings.corporate_logo_is_visible !== false)
  )
}